import { registerInstances } from "./assets.js";
import "./style.css";
import "./game-menu.css";
import "./inventory-v3.css";
import "./inventory-v4.css";
import "./menu-v5.css";
import { bindInventory } from "./inventory-controls.js";
import { Game } from "./game.js";
import { request, setCsrf, toast, escape as e } from "./api.js";
import * as pages from "./pages.js";
import { settings, saveSettings } from "./settings.js";
const root = document.querySelector<HTMLDivElement>("#app")!;
let game: Game;
try {
  game = new Game(document.querySelector<HTMLCanvasElement>("#game")!);
  await game.preview();
} catch (error) {
  root.innerHTML =
    '<div class="fatal"><h1>WebGL could not start.</h1><p>Enable browser hardware acceleration, then reload.</p><pre>' +
    e((error as Error).message) +
    "</pre></div>";
  throw error;
}
let view: pages.View = {
  user: null as any,
  maps: [],
  content: {},
  community: {},
  selected: "",
  filter: { search: "", tier: "", status: "", sort: "tier", mode: "" },
  lobby: null,
};
let page = "play",
  profileId = "",
  usersSearch = "",
  usersPage = 1,
  memberMetric = "level",
  renderGeneration = 0,
  binding = "";
let inviteAccepted = false;
const authConfig = await request("/auth/config");
async function refresh() {
  const [user, maps, content, community] = await Promise.all([
    request("/api/me"),
    request("/api/maps"),
    request("/api/content"),
    request("/api/community"),
  ]);
  content.cosmetics = [
    ...content.cosmetics.filter(
      (c: any) => !user.inventory?.some((i: any) => i.id === c.id),
    ),
    ...(user.inventory ?? []),
  ];
  await registerInstances(user.inventory ?? []);
  for (const m of maps)
    m.crateUntil = m.crateStatus?.eligible_again_at
      ? Date.now() +
        new Date(m.crateStatus.eligible_again_at).getTime() -
        new Date(m.crateStatus.server_now).getTime()
      : 0;
  setCsrf(user.csrf);
  view = { ...view, user, maps, content, community, lobby: game.network.room };
  if (!maps.some((m: any) => m.id === view.selected))
    view.selected = maps[0]?.id ?? "";
  game.identify(user, content.cosmetics);
}
async function signedIn() {
  await refresh();
  game.network.connect();
  const lobby = new URLSearchParams(location.search).get("lobby");
  if (lobby) {
    let attempts = 0;
    const timer = setInterval(() => {
      if (game.network.socket?.readyState === WebSocket.OPEN) {
        game.network.send({ type: "join", code: lobby });
        clearInterval(timer);
      } else if (++attempts > 100) clearInterval(timer);
    }, 100);
  }
  await render();
}
function header() {
  return (
    '<header><button class="wordmark" data-action="nav" data-page="play"><span class="frog-mark"><i></i><i></i></span>froghopping</button>' +
    (view.user
      ? "<nav>" +
        [
          "play",
          "maps",
          "challenges",
          "leaderboards",
          "users",
          "inventory",
          "profile",
        ]
          .map(
            (p) =>
              '<button data-action="nav" data-page="' +
              p +
              '" class="' +
              (page === p ? "active" : "") +
              '">' +
              p.toUpperCase() +
              "</button>",
          )
          .join("") +
        '</nav><div class="account"><button class="account-name" data-action="account-menu" aria-label="Account menu">' +
        (view.user.avatar
          ? '<img class="avatar" src="' + e(view.user.avatar) + '" alt="">'
          : '<span class="avatar">' +
            e(view.user.name.slice(0, 1)) +
            "</span>") +
        "<div>" +
        e(view.user.name) +
        "<span>LEVEL " +
        view.user.level +
        "</span></div><span>⌄</span></button></div>"
      : "") +
    "</header>"
  );
}
async function render() {
  if (!game.menu) return;
  const generation = ++renderGeneration;
  if (!view.user) {
    root.innerHTML =
      header() +
      pages.loginPage(authConfig.devAuth, authConfig.discord, inviteAccepted);
    root.hidden = false;
    return;
  }
  let body = "";
  try {
    if (page === "play") body = pages.playPage(view);
    if (page === "maps") body = pages.mapsPage(view);
    if (page === "challenges") body = pages.challengesPage(view);
    if (page === "inventory") body = pages.inventoryPage(view);
    if (page === "leaderboards")
      body = pages.leaderboardPage(
        view,
        view.selected
          ? await request("/api/leaderboards/" + view.selected)
          : [],
        memberMetric,
      );
    if (page === "users")
      body = pages.usersPage(
        await request(
          "/api/users?q=" +
            encodeURIComponent(usersSearch) +
            "&page=" +
            usersPage,
        ),
        usersSearch,
      );
    if (page === "profile") {
      const data = await request(
        "/api/profiles/" + (profileId || view.user.id),
      );
      await registerInstances(data.user.inventory ?? []);
      body = pages.profilePage(view, data);
    }
    if (page === "settings")
      body =
        pages.settingsPage(!!view.user.admin) +
        (authConfig.devAuth
          ? '<div class="panel narrow"><button data-action="dev-guest">SWITCH THIS CLIENT TO LOCAL GUEST</button></div>'
          : "");
    if (page === "admin")
      body = pages.adminPage(await request("/api/admin/status"), view);
  } catch (err) {
    toast((err as Error).message);
    body =
      '<section class="panel"><h1>Could not load this page.</h1><p>Try again from the navigation.</p></section>';
  }
  if (generation !== renderGeneration) return;
  if (page === "pause" && game.state)
    body = `<section class="pause-menu"><h1>${e(game.map?.name)}</h1><button data-action="resume">Resume</button><button data-action="restart-map">Restart map</button><button data-action="nav" data-page="maps">Change map</button><button data-action="nav" data-page="settings">Settings</button><button data-action="leave-map">Leave map</button></section>`;
  const room = game.network.room;
  root.innerHTML =
    header() +
    body +
    (room
      ? '<div class="lobby-bar"><span class="live-dot"></span><b>LOBBY ' +
        e(room.code) +
        "</b><span>" +
        room.players.length +
        ' / 12 PLAYERS</span><button data-action="copy-lobby">COPY INVITE ↗</button><select id="spectator" aria-label="Spectate player"><option value="">YOUR RUN</option>' +
        room.players
          .filter((p: any) => p.id !== view.user.id)
          .map(
            (p: any) =>
              '<option value="' + e(p.id) + '">' + e(p.name) + "</option>",
          )
          .join("") +
        '</select><button data-action="spectate-prev">←</button><button data-action="spectate-next">→</button><button data-action="spectate-camera">' +
        (game.thirdPerson ? "THIRD" : "FIRST") +
        ' PERSON</button><button data-action="leave">LEAVE</button></div>'
      : "") +
    (game.state
      ? '<button class="resume-run" data-action="resume">RESUME RUN ↗</button>'
      : "");
  root.hidden = false;
  const preview = root.querySelector<HTMLElement>("[data-preview-id]");
  if (preview)
    void game.renderer.showPreview(preview.dataset.previewId!).catch((err) => {
      console.error("Preview failed", err);
      toast("Could not load this item preview.");
    });
  void game.renderer.showThumbnails();
}
async function enterMap(id: string) {
  view.selected = id;
  if (game.network.room && game.network.room.mapId !== id) {
    if (game.network.room.owner !== view.user.id) {
      toast("The lobby host chooses the map. Leave the lobby to play solo.");
      return;
    }
    game.network.send({ type: "map", mapId: id });
  }
  await game.load(id);
  root.hidden = true;
  await game.enter();
}
game.onEnter = () => {
  root.hidden = true;
};
game.onMenu = () => {
  page = game.state ? "pause" : "play";
  root.hidden = false;
  void render();
};
game.onMap = () => {
  view.selected = game.map!.id;
  game.pb = view.maps.find((m) => m.id === view.selected)?.pb ?? null;
};
game.onLobby = () => {
  view.lobby = game.network.room;
  if (game.menu) void render();
};
game.onFinish = () => void refresh().catch((err) => toast(err.message));
root.addEventListener("click", async (event) => {
  const button = (event.target as Element).closest<HTMLElement>(
    "[data-action]",
  );
  if (!button) return;
  const { action, id, page: nextPage, code, team, bind } = button.dataset;
  try {
    if (action === "restart-map") {
      game.resume();
      await game.restart();
      return;
    }
    if (action === "leave-map") {
      game.leave();
      return;
    }
    if (action === "account-menu") {
      const old = root.querySelector(".account-menu");
      if (old) old.remove();
      else
        root
          .querySelector(".account")
          ?.insertAdjacentHTML(
            "beforeend",
            '<div class="account-menu"><button data-action="nav" data-page="profile">Profile</button><button data-action="nav" data-page="settings">Settings</button>' +
              (view.user.admin
                ? '<button data-action="nav" data-page="admin">Administration</button>'
                : "") +
              '<button data-action="logout">Sign out</button></div>',
          );
      return;
    }
    if (action === "dev" || action === "dev-guest") {
      game.network.close();
      await request("/auth/dev", { guest: action === "dev-guest" });
      await signedIn();
      return;
    }
    if (action === "nav") {
      if (!view.user) return;
      game.controls.release(false);
      game.menu = true;
      document.querySelector("#capture-prompt")?.remove();
      page = nextPage!;
      view.inspect = false;
      if (view.preview && !view.user.cosmetics?.includes(view.preview))
        view.preview = undefined;
      profileId = "";
      await refresh();
      await render();
    }
    if (action === "inventory-category") {
      view.inventoryCategory = button.dataset.category;
      view.preview = undefined;
      await render();
    }
    if (action === "inventory-side") {
      view.inventorySide = button.dataset.side;
      view.preview = undefined;
      await render();
    }
    if (action === "zoom-in")
      game.renderer.previewZoom = Math.max(
        0.5,
        game.renderer.previewZoom - 0.15,
      );
    if (action === "zoom-out")
      game.renderer.previewZoom = Math.min(2, game.renderer.previewZoom + 0.15);
    if (action === "inspect-item") game.renderer.inspectPreview();
    if (action === "open-crate" || action === "open-all") {
      const crates = ((view.user as any).crates ?? []).filter(
        (c: any) => c.tier === Number(button.dataset.tier),
      );
      const ids = crates
        .slice(0, action === "open-crate" ? 1 : 100)
        .map((c: any) => c.id);
      if (ids.length) {
        button.setAttribute("disabled", "");
        const result = await request("/api/crates/open", { ids });
        view.crateResults = result.results;
        await refresh();
        await render();
      }
    }
    if (action === "challenge-category") {
      view.challengeCategory = button.dataset.category;
      await render();
    }
    if (action === "challenge-preview") {
      const item = view.content.cosmetics.find((c: any) => c.id === id);
      if (item) {
        page = "inventory";
        view.inventoryCategory =
          item.category === "ct" || item.category === "t"
            ? "agents"
            : item.category === "knife"
              ? "knives"
              : "gloves";
        view.inventoryCategory = ["ct", "t"].includes(item.category)
          ? "agents"
          : item.category === "knife"
            ? "knives"
            : "gloves";
        view.inspect = true;
        view.preview = id;
        await render();
      }
    }
    if (action === "preview-item") {
      view.preview = id;
      await render();
    }
    if (action === "rotate-left") game.renderer.previewRotation -= 0.4;
    if (action === "rotate-right") game.renderer.previewRotation += 0.4;
    if (action === "play") await enterMap(id!);
    if (action === "resume") {
      root.hidden = true;
      game.resume();
    }
    if (action === "create") {
      game.network.send({ type: "create", mapId: id });
      await enterMap(id!);
    }
    if (action === "join") game.network.send({ type: "join", code });
    if (action === "leave") game.network.send({ type: "leave" });
    if (action === "copy-lobby") {
      await navigator.clipboard.writeText(
        location.origin + "/?lobby=" + game.network.room.code,
      );
      toast("Invite link copied.");
    }
    if (action === "map-filter-mode" || action === "map-filter-tier" || action === "map-filter-favourites" || action === "map-filter-clear") {
      if(action === "map-filter-mode") view.filter.mode = view.filter.mode === id ? "" : id!;
      else if(action === "map-filter-tier") view.filter.tier = view.filter.tier === id ? "" : id!;
      else if(action === "map-filter-favourites") view.filter.status = view.filter.status === "favourite" ? "" : "favourite";
      else { view.filter.tier = ""; view.filter.status = ""; view.filter.search = ""; view.filter.mode = ""; }
      await render();
      return;
    }
    if (action === "favourite") {
      await request("/api/maps/" + id + "/favourite", {});
      await refresh();
      await render();
    }
    if (action === "board") {
      view.selected = id!;
      page = "leaderboards";
      await render();
    }
    if (action === "users-page") {
      usersPage = Number(id);
      await render();
    }
    if (action === "profile") {
      profileId = id!;
      page = "profile";
      await render();
    }
    if (action === "equip") {
      await request("/api/equip", { id });
      await refresh();
      await render();
      game.audio.play("ui");
    }

    if (action === "replay") {
      await game.replay(id!);
      root.hidden = true;
    }
    if (action === "ghost") await game.replay(id!, true);
    if (action === "spectate-prev" || action === "spectate-next") {
      game.cycleSpectator(action === "spectate-next" ? 1 : -1);
      if (game.spectating) root.hidden = true;
    }
    if (action === "spectate-camera") {
      game.thirdPerson = !game.thirdPerson;
      await render();
    }
    if (action === "fullscreen") {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    }
    if (action === "debug") {
      game.debug = !game.debug;
      toast("Debug overlay " + (game.debug ? "enabled" : "disabled"));
    }
    if (action === "noclip") game.toggleNoclip();
    if (action === "bind") {
      binding = bind!;
      button.textContent = "PRESS A KEY…";
    }
    if (action === "logout") {
      await request("/auth/logout", {});
      game.network.close();
      game.state = undefined;
      view.user = null as any;
      await render();
    }
  } catch (err) {
    toast((err as Error).message);
  }
});
root.addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.target as HTMLFormElement,
    data = Object.fromEntries(new FormData(form));
  try {
    if (form.id === "invite-form") {
      await request("/auth/invite", { code: data.code });
      inviteAccepted = true;
      await render();
      return;
    }
    if (form.id === "join-form") {
      game.network.send({ type: "join", code: data.code });
      return;
    }
    if (form.id === "settings-form") {
      applySettingsForm(form);
      toast("Settings saved.");
      return;
    }
    const actions: Record<string, any> = {
      "grant-form": { ...data, enabled: data.enabled === "true" },
      "invalidate-form": { action: "invalidate", ...data },
      "map-admin-form": {
        action: "map",
        ...data,
        tier: Number(data.tier),
        enabled: data.enabled === "true",
      },
      "challenge-admin-form": {
        action: "challenge",
        ...data,
        target: Number(data.target),
        xp: Number(data.xp),
      },
    };
    if (actions[form.id]) {
      await request("/api/admin/action", actions[form.id]);
      toast("Server updated.");
      await refresh();
      await render();
    }
  } catch (err) {
    toast((err as Error).message);
  }
});
root.addEventListener("change", async (event) => {
  const el = event.target as HTMLInputElement;
  try {
    if (el.dataset.filter) {
      (view.filter as any)[el.dataset.filter] = el.value;
      await render();
    }
    if (el.id === "board-map") {
      view.selected = el.value;
      await render();
    }
    if (el.id === "member-metric") {
      memberMetric = el.value;
      await render();
    }
    if (el.id === "spectator") {
      game.spectate(el.value);
      if (el.value) root.hidden = true;
    }
  } catch (err) {
    toast((err as Error).message);
  }
});
function applySettingsForm(form: HTMLFormElement) {
  for (const el of [...form.elements]) {
    if (
      !(el instanceof HTMLInputElement || el instanceof HTMLSelectElement) ||
      !el.name ||
      !(el.name in settings)
    )
      continue;
    (settings as any)[el.name] =
      el instanceof HTMLInputElement && el.type === "checkbox"
        ? el.checked
        : typeof (settings as any)[el.name] === "number"
          ? Number(el.value)
          : el.value;
  }
  saveSettings();
  game.renderer.resize();
  game.audio.applySettings();
}
root.addEventListener("input", (event) => {
  const el = event.target as HTMLElement;
  const form = el.closest<HTMLFormElement>("#settings-form");
  if (form) applySettingsForm(form);
});
root.addEventListener("change", (event) => {
  const el = event.target as HTMLElement;
  const form = el.closest<HTMLFormElement>("#settings-form");
  if (form) applySettingsForm(form);
});
let searchTimer: ReturnType<typeof setTimeout>;
root.addEventListener("input", (event) => {
  const el = event.target as HTMLInputElement;
  if (el.type === "range" && el.nextElementSibling)
    el.nextElementSibling.textContent = el.value;
  if (el.id === "users-search") {
    usersSearch = el.value;
    usersPage = 1;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      if (page !== "users") return;
      const cursor = el.selectionStart;
      await render();
      const input = document.querySelector<HTMLInputElement>("#users-search");
      input?.focus();
      if (input?.type !== "search") input?.setSelectionRange(cursor, cursor);
    }, 200);
  }
  if (el.id === "map-search") {
    view.filter.search = el.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      const cursor = el.selectionStart;
      await render();
      const input = document.querySelector<HTMLInputElement>("#map-search");
      input?.focus();
      input?.setSelectionRange(cursor, cursor);
    }, 200);
  }
});
document.addEventListener("keydown", (event) => {
  if (binding) {
    event.preventDefault();
    if (event.code !== "Escape") {
      const current = settings.binds[binding] ?? [];
      settings.binds[binding] = current.includes(event.code)
        ? current.filter((k) => k !== event.code)
        : [...current, event.code].slice(-3);
      saveSettings();
    }
    binding = "";
    void render();
  }
});
document.addEventListener("pointerdown", (event) => {
  if (!(event.target as Element).closest(".account"))
    root.querySelector(".account-menu")?.remove();
});
document.addEventListener("keydown", (event) => {
  if (event.code === "Escape") root.querySelector(".account-menu")?.remove();
});
bindInventory(root, () => view, game, render, refresh);
try {
  await signedIn();
} catch {
  await render();
}

setInterval(() => {
  document.querySelectorAll<HTMLElement>("[data-crate-until]").forEach((el) => {
    el.textContent = pages.crateCountdown(Number(el.dataset.crateUntil));
  });
}, 1000);
